import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { qaApi, QaResult, FactRow } from '@/lib/qa';
import { formatMoney } from '@/lib/currency';
import { AppShell } from '@/components/app/AppShell';
import { MarkdownText } from '@/components/MarkdownText';
import { useToast, errMessage } from '@/components/Toast';

type Turn = { role: 'user'; question: string } | { role: 'agent'; result: QaResult };

const HISTORY_KEY_PREFIX = 'cadencez.qa.history.';

function loadHistory(wsId: string, agent: 'qa' | 'audit'): Turn[] {
  try {
    const raw = localStorage.getItem(`${HISTORY_KEY_PREFIX}${wsId}.${agent}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Turn[];
    return Array.isArray(parsed) ? parsed.slice(-50) : [];
  } catch {
    return [];
  }
}

function saveHistory(wsId: string, agent: 'qa' | 'audit', history: Turn[]) {
  try {
    localStorage.setItem(`${HISTORY_KEY_PREFIX}${wsId}.${agent}`, JSON.stringify(history.slice(-50)));
  } catch {
    /* quota or disabled — skip */
  }
}

export function QaPage() {
  const { wsId } = useParams<{ wsId: string }>();
  const { t } = useTranslation();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const [agent, setAgent] = useState<'qa' | 'audit'>('qa');
  // Seed from ?q= so other pages (e.g. Account Search) can deep-link a
  // prefilled question; the user still reviews and presses send.
  const [question, setQuestion] = useState(() => searchParams.get('q') ?? '');
  const [history, setHistory] = useState<Turn[]>(() => (wsId ? loadHistory(wsId, 'qa') : []));
  const [drawerIds, setDrawerIds] = useState<string[] | null>(null);

  // Swap history when the agent flips
  useEffect(() => {
    if (wsId) setHistory(loadHistory(wsId, agent));
  }, [agent, wsId]);

  // Persist after every turn
  useEffect(() => {
    if (wsId) saveHistory(wsId, agent, history);
  }, [wsId, agent, history]);

  const ask = useMutation({
    mutationFn: async () => qaApi.ask(wsId!, { agentKind: agent, question }),
    onSuccess: (result) => {
      setHistory((h) => [...h, { role: 'agent', result }]);
      setQuestion('');
    },
    onError: (e) => {
      // Drop the dangling user turn so the thread doesn't show an unanswered
      // question, and surface why (cost cap, plan gate, network…).
      setHistory((h) => (h[h.length - 1]?.role === 'user' ? h.slice(0, -1) : h));
      toast.error(errMessage(e, t('qa.error', { defaultValue: 'The assistant could not answer that. Please try again.' })));
    },
  });

  function submit() {
    if (!question.trim()) return;
    setHistory((h) => [...h, { role: 'user', question }]);
    ask.mutate();
  }
  function clearChat() {
    setHistory([]);
    if (wsId) saveHistory(wsId, agent, []);
  }

  return (
    <AppShell title={t('qa.title')} subtitle={t('qa.subtitle')}>
      <main className="mx-auto max-w-4xl px-6 py-10 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-semibold text-foreground tracking-tight">{t('qa.title')}</h1>
            <p className="mt-1 text-ink-600">{t('qa.subtitle')}</p>
          </div>
          {history.length > 0 && (
            <button
              onClick={clearChat}
              className="px-3 py-1.5 rounded-md border border-border text-ink-700 hover:bg-foreground/[0.04] text-xs"
            >
              {t('qa.clear_chat')}
            </button>
          )}
        </div>

        <div className="flex items-center gap-1 text-xs">
          {(['qa', 'audit'] as const).map((k) => (
            <button
              key={k}
              onClick={() => setAgent(k)}
              className={
                'px-3 py-1.5 rounded-md font-medium transition ' +
                (agent === k ? 'bg-foreground text-background' : 'text-ink-600 hover:bg-foreground/[0.04]')
              }
            >
              {k === 'qa' ? t('qa.agent_qa') : t('qa.agent_audit')}
            </button>
          ))}
        </div>

        <div className="rounded-md border border-border bg-white p-5 space-y-4 min-h-[200px]">
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('qa.history_empty')}</p>
          ) : (
            history.map((turn, i) =>
              turn.role === 'user' ? (
                <UserTurn key={i} question={turn.question} />
              ) : (
                <AgentTurn
                  key={i}
                  result={turn.result}
                  t={t}
                  onShowCitations={(ids) => setDrawerIds(ids)}
                />
              ),
            )
          )}
          {ask.isPending && <div className="text-sm text-muted-foreground">{t('qa.asking')}</div>}
        </div>

        <div className="rounded-md border border-border bg-white p-3 flex gap-2 sticky bottom-2">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('qa.placeholder')}
            rows={3}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submit();
            }}
            className="flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-500"
          />
          <button
            onClick={submit}
            disabled={ask.isPending || !question.trim()}
            className="self-end px-5 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-60 transition"
          >
            {ask.isPending ? t('qa.asking') : t('qa.send')}
          </button>
        </div>
      </main>

      {drawerIds && wsId && (
        <CitationsDrawer
          wsId={wsId}
          ids={drawerIds}
          onClose={() => setDrawerIds(null)}
          t={t}
        />
      )}
    </AppShell>
  );
}

function UserTurn({ question }: { question: string }) {
  return (
    <div className="flex justify-end">
      <div className="rounded-md bg-foreground text-background px-4 py-2 text-sm max-w-[80%] whitespace-pre-wrap">
        {question}
      </div>
    </div>
  );
}

function AgentTurn({
  result,
  t,
  onShowCitations,
}: {
  result: QaResult;
  t: (k: string, opts?: Record<string, unknown>) => string;
  onShowCitations: (ids: string[]) => void;
}) {
  const citationCount = result.citedFactIds.length;
  return (
    <div className="flex justify-start">
      <div className="rounded-md bg-ink-50 border border-border px-4 py-3 max-w-[85%]">
        <div className="text-xs text-emerald-700 font-semibold mb-1.5">
          ✦ {t('variance.ai_byline')}
        </div>
        <MarkdownText>{result.reply}</MarkdownText>
        {citationCount > 0 && (
          <button
            onClick={() => onShowCitations(result.citedFactIds.map(String))}
            className="mt-2 text-xs text-emerald-700 font-semibold hover:underline"
          >
            {t('qa.view_citations', { count: citationCount })}
          </button>
        )}
      </div>
    </div>
  );
}

function CitationsDrawer({
  wsId,
  ids,
  onClose,
  t,
}: {
  wsId: string;
  ids: string[];
  onClose: () => void;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const { data: facts = [], isLoading } = useQuery({
    queryKey: ['facts', wsId, ids.join(',')],
    queryFn: () => qaApi.facts(wsId, ids),
  });

  function exportCsv() {
    const header = ['fact_id', 'scenario', 'account_code', 'amount', 'period_start', 'period_end', 'source_row'];
    const esc = (v: unknown) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = facts.map((f) =>
      [f.factId, f.scenario, f.accountCode, f.amount, f.periodStart, f.periodEnd, f.sourceRowRef ?? ''].map(esc).join(','),
    );
    const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'cadencez-cited-facts.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <button
        type="button"
        onClick={onClose}
        className="absolute inset-0 bg-foreground/30 backdrop-blur-sm"
        aria-label={t('qa.close')}
      />
      <aside className="relative w-full max-w-md h-full bg-background border-l border-border shadow-xl flex flex-col">
        <header className="flex items-center justify-between px-5 h-14 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">{t('qa.citation_drawer_title')}</h3>
          <div className="flex items-center gap-1">
            {facts.length > 0 && (
              <button
                onClick={exportCsv}
                className="text-xs text-emerald-700 hover:text-emerald-800 font-semibold px-2 py-1 rounded-md hover:bg-emerald-500/10"
              >
                {t('qa.export_csv')}
              </button>
            )}
            <button
              onClick={onClose}
              className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 rounded-md hover:bg-foreground/[0.04]"
            >
              {t('qa.close')}
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : facts.length === 0 ? (
            <p className="text-sm text-muted-foreground">—</p>
          ) : (
            facts.map((f) => <FactCard key={f.factId} fact={f} wsId={wsId} />)
          )}
        </div>
      </aside>
    </div>
  );
}

function FactCard({ fact, wsId }: { fact: FactRow; wsId: string }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="num text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          #{fact.factId} · {fact.scenario}
        </span>
        <span className="num text-[11px] text-muted-foreground">
          {fact.periodStart} → {fact.periodEnd}
        </span>
      </div>
      <div className="flex items-baseline justify-between">
        <Link to={`/app/workspace/${wsId}/account-search?q=${encodeURIComponent(fact.accountCode)}`} className="num text-sm font-semibold text-foreground hover:text-emerald-700 hover:underline">
          {fact.accountCode}
        </Link>
        <span className="num text-sm text-foreground tabular-nums">
          {formatMoney(fact.amount, { maximumFractionDigits: 0 })}
        </span>
      </div>
      {fact.dim && Object.keys(fact.dim).length > 0 && (
        <div className="mt-2 text-[11px] text-muted-foreground num">
          {Object.entries(fact.dim).map(([k, v]) => (
            <span key={k} className="inline-block mr-2">
              {k}: <span className="text-ink-700">{String(v)}</span>
            </span>
          ))}
        </div>
      )}
      {fact.sourceRowRef && (
        <div className="mt-1 text-[10px] text-ink-400 num">src: {fact.sourceRowRef}</div>
      )}
    </div>
  );
}
